import { Box, Stack, Typography } from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import type { InsightKind } from './resultsMockData'

type AutoInsightsProps = {
  /** i18n key prefix: 'insights' (single) or 'multi.insights' (multi). */
  ns: 'insights' | 'multi.insights'
  items: { kind: InsightKind; textKey: string }[]
  /** Show the subtitle + beta tag in the header (single variant only). */
  showSub?: boolean
}

/** Navy gradient card with heuristic auto-generated insights. Shared by the
 *  single and multi variants. */
export default function AutoInsights({ ns, items, showSub }: AutoInsightsProps) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('results')

  const tagStyle = (kind: InsightKind) => {
    if (kind === 'positive') return { bg: alpha(c.success, 0.16), fg: '#9FE5D9' }
    if (kind === 'warning') return { bg: alpha(c.warning, 0.16), fg: '#FFD089' }
    return { bg: 'rgba(255,255,255,.1)', fg: 'rgba(255,255,255,.7)' }
  }
  const tagLabel = (kind: InsightKind) =>
    kind === 'positive' ? t(`${ns}.tagPositive`) : kind === 'warning' ? t(`${ns}.tagWarning`) : t(`${ns}.tagNeutral`)

  return (
    <Box
      sx={{
        background: 'linear-gradient(135deg,#1B1C38,#252745)',
        borderRadius: '18px',
        p: 2.75,
        color: '#fff',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.75,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, position: 'relative' }}>
        <Box sx={{ width: 32, height: 32, borderRadius: '10px', bgcolor: alpha(c.coral, 0.22), display: 'grid', placeItems: 'center' }}>
          <AutoAwesomeIcon sx={{ fontSize: 18, color: c.coral }} />
        </Box>
        <Stack spacing={0}>
          <Typography sx={{ fontFamily: theme.affexy.fonts.heading, fontWeight: 700, fontSize: 16 }}>{t(`${ns}.title`)}</Typography>
          {showSub && (
            <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,.6)' }}>{t('insights.sub')}</Typography>
          )}
        </Stack>
        <Typography sx={{ ml: 'auto', fontSize: 11, color: 'rgba(255,255,255,.5)' }}>{t('insights.beta')}</Typography>
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3,1fr)' }, gap: 1.75, position: 'relative' }}>
        {items.map((it, i) => {
          const ts = tagStyle(it.kind)
          return (
            <Stack
              key={i}
              spacing={1}
              sx={{ p: 1.75, bgcolor: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.08)', borderRadius: '14px' }}
            >
              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  px: 1.125,
                  py: 0.375,
                  borderRadius: `${theme.affexy.radii.pill}px`,
                  bgcolor: ts.bg,
                  color: ts.fg,
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: '.06em',
                  textTransform: 'uppercase',
                  width: 'fit-content',
                }}
              >
                {tagLabel(it.kind)}
              </Box>
              <Typography sx={{ fontSize: 13.5, color: '#fff', lineHeight: 1.55 }}>{t(`${ns}.${it.textKey}`)}</Typography>
            </Stack>
          )
        })}
      </Box>
    </Box>
  )
}
